import { useMutation } from '@apollo/client';
import React from 'react';

import { cacheUtils } from '@eluve/apollo-client';
import { SimpleTooltip } from '@eluve/components';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Icon,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

const deleteSiteBannerMutation = graphql(`
  mutation deleteSiteBanner($siteBannerId: uuid!) {
    deleteSiteBannersByPk(id: $siteBannerId) {
      id
    }
  }
`);

export interface DeleteSiteBannerActionProps {
  siteBannerId: string;
}

export const DeleteSiteBannerAction: React.FC<DeleteSiteBannerActionProps> = ({
  siteBannerId,
}) => {
  const toast = useToast();
  const [deleteSiteBanner] = useMutation(deleteSiteBannerMutation, {
    onError: () => toast.error('Failed to delete site banner'),
    optimisticResponse: () => ({
      deleteSiteBannersByPk: {
        __typename: 'SiteBanners' as const,
        id: siteBannerId,
      },
    }),
    update: (cache) => {
      cacheUtils.evict({
        typeName: 'SiteBanners',
        key: { id: siteBannerId },
      });
      cache.gc();
    },
  });

  const handleDelete = () => deleteSiteBanner({ variables: { siteBannerId } });

  return (
    <AlertDialog>
      <SimpleTooltip label="Delete site banner">
        <AlertDialogTrigger asChild>
          <Icon
            name="CircleMinus"
            size="xl"
            className="cursor-pointer text-bg-negative-strong-primary"
          />
        </AlertDialogTrigger>
      </SimpleTooltip>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This will delete site banner permanently.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel autoFocus>Cancel</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={handleDelete}>
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
